import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader, KpiCard } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2, AlertCircle, Users, ClipboardCheck } from "lucide-react";

const VOLET_LABEL = {
  epi: "Épidémiologie", lab: "Laboratoire", rcce: "CREC / RCCE",
  eds: "EDS / SDB", ipc: "PCI", case_mgmt: "Prise en charge",
};

export default function EntryRegister() {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/harmonized/compliance", { params: { date: day } });
        setData(res.data);
      } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [day]);

  const rate = data && data.total ? Math.round((data.submitted / data.total) * 100) : null;
  const missing = data ? data.agents.filter((a) => !a.submitted) : [];

  return (
    <div data-testid="entry-register-page">
      <PageHeader eyebrow={t("Suivi de complétude")} title={t("Registre de saisie")}
        subtitle={t("Qui a saisi et qui n'a pas encore saisi, pour relancer les agents en retard.")}
        testid="register-header" />

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4 sm:w-64">
        <Label className="text-xs">{t("Date")}</Label>
        <Input type="date" value={day} max={today} onChange={(e) => setDay(e.target.value)} data-testid="register-date" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>
      ) : !data ? null : (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <KpiCard label={t("Agents attendus")} value={data.total} icon={Users} tone="slate" testid="register-total" />
            <KpiCard label={t("Ont saisi")} value={data.submitted} icon={ClipboardCheck} tone="primary" testid="register-submitted" />
            <KpiCard label={t("Taux de saisie")} value={rate === null ? "N/A" : `${rate}%`} icon={CheckCircle2} tone="teal" testid="register-rate" />
          </div>

          {missing.length > 0 && (
            <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="register-missing">
              <p className="flex items-center gap-2 font-semibold"><AlertCircle className="h-4 w-4" />{t("Agents à relancer")} ({missing.length})</p>
              <p className="mt-1 text-xs">{missing.map((a) => a.name).join(" · ")}</p>
            </div>
          )}

          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#F1F5F9] text-left">
                  {["Agent", "Matricule", "Pilier", "Province", "Zone de santé", "Saisie du jour", "Champs", "Dernière saisie", "7 derniers jours"].map((h) => (
                    <th key={h} className="p-3 text-xs font-bold text-slate-600">{t(h)}</th>))}
                </tr></thead>
                <tbody>
                  {data.agents.length === 0 ? (
                    <tr><td colSpan={9} className="p-8 text-center text-sm text-slate-400">{t("Aucun agent dans votre périmètre.")}</td></tr>
                  ) : data.agents.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100 hover:bg-[#F8FAFC]" data-testid={`register-row-${a.id}`}>
                      <td className="p-3 font-medium text-slate-800">{a.name}</td>
                      <td className="p-3 font-mono text-xs text-slate-500">{a.matricule || "-"}</td>
                      <td className="p-3 text-slate-600">{t(VOLET_LABEL[a.volet] || a.volet)}</td>
                      <td className="p-3 text-slate-600">{a.province || "-"}</td>
                      <td className="p-3 text-slate-600">{a.health_zone || "-"}</td>
                      <td className="p-3">
                        {a.submitted
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />{t("Saisie faite")}</span>
                          : <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#B91C1C]"><AlertCircle className="h-3.5 w-3.5" />{t("Manquante")}</span>}
                      </td>
                      <td className="p-3 text-slate-600">{a.fields_filled}</td>
                      <td className="p-3 text-slate-500 text-xs">{a.last_entry_date || "-"}</td>
                      <td className="p-3 text-slate-600">{a.entries_last_7d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
